import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ANALYTICS_SESSION_STORAGE_KEY, storeAnalyticsSessionId } from "../lib/analyticsClient";
const HEARTBEAT_INTERVAL_MS = 30_000;

type AnalyticsAttributionPayload = {
  referrerHost?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  landingPath: string;
};

function isBlockedPath(pathname: string) {
  return (
    pathname === "/admin" ||
    pathname.startsWith("/admin/") ||
    pathname === "/dev" ||
    pathname.startsWith("/dev/") ||
    pathname === "/report-render" ||
    pathname.startsWith("/report-render/")
  );
}

function safePath(pathname: string) {
  if (isBlockedPath(pathname)) return null;
  if (pathname.startsWith("/report/")) return "/report/[report]";
  return pathname || "/";
}

function safeClientAttributionValue(value: string | null, maxLength = 120) {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > maxLength) return undefined;
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return undefined;
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) || trimmed.startsWith("//")) return undefined;
  if (trimmed.includes("@")) return undefined;
  return trimmed;
}

function getReferrerHost() {
  const referrer = document.referrer.trim();
  if (!referrer || referrer.length > 500 || /[\u0000-\u001F\u007F]/.test(referrer)) return undefined;

  try {
    const parsed = new URL(referrer);
    if (parsed.hostname === window.location.hostname) return undefined;
    return safeClientAttributionValue(parsed.hostname.toLowerCase().replace(/^www\./, ""), 120);
  } catch {
    return undefined;
  }
}

function buildAttributionPayload(path: string): AnalyticsAttributionPayload {
  const params = new URLSearchParams(window.location.search);
  const referrerHost = getReferrerHost();
  const utmSource = safeClientAttributionValue(params.get("utm_source"), 80);
  const utmMedium = safeClientAttributionValue(params.get("utm_medium"), 80);
  const utmCampaign = safeClientAttributionValue(params.get("utm_campaign"), 120);

  return {
    ...(referrerHost ? { referrerHost } : {}),
    ...(utmSource ? { utmSource } : {}),
    ...(utmMedium ? { utmMedium } : {}),
    ...(utmCampaign ? { utmCampaign } : {}),
    landingPath: path
  };
}

async function sendAnalytics(eventType: "page_view" | "heartbeat", path: string) {
  const sessionId = localStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY) ?? undefined;
  const payload = {
    sessionId,
    eventType,
    path,
    ...(eventType === "page_view" ? { attribution: buildAttributionPayload(path) } : {})
  };
  const response = await fetch("/api/analytics/track", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload),
    keepalive: eventType === "heartbeat"
  });

  if (!response.ok) return;
  const data = (await response.json().catch(() => null)) as { sessionId?: string } | null;
  if (data?.sessionId) {
    storeAnalyticsSessionId(data.sessionId);
  }
}

function sendFinalHeartbeat(path: string) {
  const sessionId = localStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY) ?? undefined;
  const payload = JSON.stringify({
    sessionId,
    eventType: "heartbeat",
    path
  });

  if (navigator.sendBeacon) {
    const blob = new Blob([payload], { type: "application/json" });
    navigator.sendBeacon("/api/analytics/track", blob);
    return;
  }

  void fetch("/api/analytics/track", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: payload,
    keepalive: true
  }).catch(() => {
    // Analytics must never interrupt the user experience.
  });
}

export function useAnalyticsTracking() {
  const location = useLocation();
  const currentPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = safePath(location.pathname);
    currentPathRef.current = path;
    if (!path) return;

    void sendAnalytics("page_view", path).catch(() => {
      // Analytics must never interrupt the user experience.
    });
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function heartbeat() {
      const path = currentPathRef.current;
      if (!path || document.visibilityState !== "visible") return;
      void sendAnalytics("heartbeat", path).catch(() => {
        // Analytics must never interrupt the user experience.
      });
    }

    function finalHeartbeat() {
      const path = currentPathRef.current;
      if (!path) return;
      sendFinalHeartbeat(path);
    }

    const intervalId = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") heartbeat();
      else finalHeartbeat();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", finalHeartbeat);
    window.addEventListener("beforeunload", finalHeartbeat);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", finalHeartbeat);
      window.removeEventListener("beforeunload", finalHeartbeat);
    };
  }, []);
}
